import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import useSortableTable from '../common/useSortableTable';
import { usePermissions } from '../../services/PermissionsContext';

const API_BASE_URL = '/api/admin/payable-charges/';
const DEPARTMENTS_API_URL = '/api/admin/departments/?include_internal=1';

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
    'Content-Type': 'application/json'
});

const PayableChargesList = () => {
    const [charges, setCharges] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    
    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentCharge, setCurrentCharge] = useState({ id: null, department: '', name: '', add_vat: false, cash: false, document_required: false, document_types: '' });

    // Expansion State
    const [expandedDepartments, setExpandedDepartments] = useState(new Set());

    // const { hasPermission } = usePermissions(); // Use if needed

    const {
        items: sortedCharges,
        requestSort,
        getSortIndicator
    } = useSortableTable(charges, { key: 'name', direction: 'ascending' });

    useEffect(() => {
        fetchDepartments();
        // Fetch charges on mount
        fetchCharges(); 
    }, []);

    const fetchDepartments = async () => {
        try {
            const res = await fetch(DEPARTMENTS_API_URL, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setDepartments(data.map(d => ({ id: d.id, text: d.name })));
            }
        } catch (error) {
            console.error("Error fetching departments", error);
        }
    };

    const fetchCharges = async () => {
        try {
            let url = API_BASE_URL;
            const params = new URLSearchParams();
            if (selectedDepartment) params.append('department_id', selectedDepartment);
            if (searchQuery) params.append('search', searchQuery);
            // Default ordering by department name, then charge name is handled by backend usually, 
            // but we can ensure it here if needed or rely on grouping logic.
            if (params.toString()) url += `?${params.toString()}`;

            const res = await fetch(url, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                const results = data.results ? data.results : data;
                setCharges(results);
            }
        } catch (error) {
            console.error("Error fetching charges", error);
        }
    };

    // Re-fetch when filters change (Server-side filtering)
    useEffect(() => {
        fetchCharges();
    }, [selectedDepartment, searchQuery]);

    // Grouping Logic
    const groupedCharges = sortedCharges.reduce((acc, charge) => {
        const deptName = charge.department_name && charge.department_name.trim() !== '' ? charge.department_name : 'UNASSIGNED';
        // We use department Name as key for sorting, but we need ID for uniqueness if tracking by ID
        // Assuming department names are unique enough for display grouping.
        // Actually, let's use the ID as the key in a Map if we wanted strictness, but for UI, Name is the header.
        if (!acc[deptName]) {
            acc[deptName] = {
                id: charge.department || 'unassigned',
                name: deptName,
                charges: []
            };
        }
        acc[deptName].charges.push(charge);
        return acc;
    }, {});

    const sortedGroups = Object.keys(groupedCharges).sort().map(key => groupedCharges[key]);

    // Auto-Expand Effect
    useEffect(() => {
        if (selectedDepartment || searchQuery) {
            // If filtering is active, expand all returned groups
            const allIds = new Set(sortedGroups.map(g => g.id));
            setExpandedDepartments(allIds);
        } else {
            // Optional: Collapse all or keep state? 
            // The prompt implies "if a user Filters... displays... already expanded".
            // It doesn't strictly say contract otherwise, but let's reset to collapsed if no filter? 
            // Or just leave it alone. Leaving it alone is better UX usually.
            // But if we want to mimic "filtering reveals", maybe we start collapsed.
        }
    }, [charges]); // depend on charges result

    const toggleDepartment = (deptId) => {
        const newSet = new Set(expandedDepartments);
        if (newSet.has(deptId)) {
            newSet.delete(deptId);
        } else {
            newSet.add(deptId);
        }
        setExpandedDepartments(newSet);
    };


    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this charge?")) return;
        try {
            const res = await fetch(`${API_BASE_URL}${id}/`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            if (res.ok) {
                toast.success("Charge deleted successfully");
                fetchCharges();
            } else {
                toast.error("Failed to delete charge");
            }
        } catch (error) {
            console.error("Error deleting charge", error);
        }
    };

    const handleEdit = (charge) => {
        setCurrentCharge({ id: charge.id, department: charge.department, name: charge.name, add_vat: charge.add_vat, cash: charge.cash, document_required: charge.document_required, document_types: charge.document_types || '' });
        setModalMode('edit');
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setCurrentCharge({ id: null, department: '', name: '', add_vat: false, cash: false, document_required: false, document_types: '' });
        setModalMode('add');
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        
        if (!currentCharge.department || !currentCharge.name) {
            toast.error("Department and Name are required");
            return;
        }
        if (currentCharge.document_required && !currentCharge.document_types.trim()) {
            toast.error("Document Types is required when Doc Required is checked");
            return;
        }

        const method = modalMode === 'edit' ? 'PUT' : 'POST';
        const url = modalMode === 'edit' ? `${API_BASE_URL}${currentCharge.id}/` : API_BASE_URL;

        try {
            const res = await fetch(url, {
                method: method,
                headers: getAuthHeaders(),
                body: JSON.stringify(currentCharge)
            });
            if (res.ok) {
                toast.success(`Charge ${modalMode === 'edit' ? 'updated' : 'created'} successfully`);
                setIsModalOpen(false);
                fetchCharges();
            } else {
                const data = await res.json();
                const errMsg = Object.entries(data).map(([k,v]) => `${k}: ${v}`).join(', ');
                toast.error("Failed to save: " + errMsg);
            }
        } catch (error) {
           toast.error("Error saving charge");
        }
    };

    return (
        <div className="payable-charges-list-container" style={{ padding: '20px' }}>
            <h2>PAYABLE CHARGES</h2>

            <div
                className="toolbar"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: 16,
                    flexWrap: 'nowrap'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'nowrap' }}>
                    <input 
                        type="text" 
                        placeholder="SEARCH CHARGES..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                        style={{ minWidth: 220, maxWidth: 260, padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}
                    />
                    <Select2Dropdown
                        options={[{id: '', text: 'ALL DEPARTMENTS'}, ...departments.map(d => ({...d, text: d.text.toUpperCase()}))]}
                        value={selectedDepartment}
                        onChange={setSelectedDepartment}
                        placeholder="FILTER BY DEPARTMENT"
                        valueKey="id"
                        displayKey="text"
                        style={{ width: 250 }}
                    />
                </div>
                <button onClick={handleAdd} style={{ marginLeft: 'auto', minWidth: 150, background: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 18px', fontSize: '14px', cursor: 'pointer' }}>
                    ADD CHARGE
                </button>
            </div>

            <div className="grouped-charges-container" style={{ background: '#fff', boxShadow: '0 0 8px rgba(0,0,0,0.08)', borderRadius: '8px', overflow: 'hidden', padding: '2px' }}>
                {sortedGroups.length === 0 && (
                     <div style={{ textAlign: 'center', padding: '30px', color: '#888', fontStyle: 'italic' }}>NO CHARGES FOUND</div>
                )}

                {sortedGroups.map(group => {
                    const isExpanded = expandedDepartments.has(group.id);
                    return (
                        <div key={group.id} className="department-group" style={{ marginBottom: '2px' }}>
                             {/* Group Header */}
                            <div 
                                onClick={() => toggleDepartment(group.id)}
                                style={{ 
                                    padding: '12px 16px', 
                                    background: '#f1f7ff', 
                                    borderBottom: '1px solid #e0e0e0',
                                    borderTop: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    fontWeight: 'bold',
                                    color: '#0056b3',
                                    userSelect: 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ width: '15px' }}></i>
                                    <span>{group.name.toUpperCase()}</span>
                                    <span style={{ fontSize: '11px', background: '#dcebff', padding: '2px 8px', borderRadius: '10px', color: '#004085' }}>
                                        {group.charges.length}
                                    </span>
                                </div>
                            </div>

                            {/* Group Content */}
                            {isExpanded && (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th 
                                                onClick={() => requestSort('name')}
                                                style={{ 
                                                    padding: '10px 16px 10px 45px', 
                                                    textAlign: 'left', 
                                                    fontSize: '12px', 
                                                    color: '#555', 
                                                    borderBottom: '1px solid #eee', 
                                                    width: '40%',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    fontWeight: 'bold'
                                                }}
                                                title="Click to sort"
                                            >
                                                CHARGE NAME 
                                                {getSortIndicator('name')}
                                            </th>
                                            <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: '12px', color: '#555', borderBottom: '1px solid #eee', width: '15%' }}>ADD VAT</th>
                                            <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: '12px', color: '#555', borderBottom: '1px solid #eee', width: '15%' }}>CASH/OFFICE/CHEQUE</th>
                                            <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: '12px', color: '#555', borderBottom: '1px solid #eee', width: '15%' }}>DOC REQUIRED</th>
                                            <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '12px', color: '#555', borderBottom: '1px solid #eee' }}>ACTION</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.charges.map(charge => (
                                            <tr key={charge.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                <td style={{ padding: '8px 16px', paddingLeft: '45px', fontSize: '13px', color: '#333' }}>
                                                    {charge.name.toUpperCase()}
                                                </td>
                                                <td style={{ padding: '8px 16px', textAlign: 'center', fontSize: '16px' }}>
                                                    {charge.add_vat ? (
                                                        <i className="fa fa-check" style={{ color: 'green' }} title="VAT Added"></i>
                                                    ) : (
                                                        <i className="fa fa-times" style={{ color: 'red' }} title="No VAT"></i>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px 16px', textAlign: 'center', fontSize: '16px' }}>
                                                    {charge.cash ? (
                                                        <i className="fa fa-check" style={{ color: 'green' }} title="Cash"></i>
                                                    ) : (
                                                        <i className="fa fa-times" style={{ color: 'red' }} title="Not Cash"></i>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px 16px', textAlign: 'center', fontSize: '16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                        {charge.document_required ? (
                                                            <i className="fa fa-check" style={{ color: 'green' }} title="Doc Required"></i>
                                                        ) : (
                                                            <i className="fa fa-times" style={{ color: 'red' }} title="No Doc Required"></i>
                                                        )}
                                                        {charge.document_required && charge.document_types && (
                                                            <span style={{ fontSize: '11px', color: '#333', fontWeight: 'bold' }}>{charge.document_types}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleEdit(charge); }} 
                                                        style={{ background: 'none', border: 'none', color: '#007bff', fontSize: '16px', cursor: 'pointer', marginRight: '10px' }}
                                                        title="Edit"
                                                    >
                                                        <i className="fa fa-pencil-alt"></i>
                                                    </button>
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(charge.id); }} 
                                                        style={{ background: 'none', border: 'none', color: '#dc3545', fontSize: '16px', cursor: 'pointer' }}
                                                        title="Delete"
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    );
                })}
            </div>

            {isModalOpen && (
                <div className="modal-backdrop">
                    <div className="dialog-container" style={{ width: '500px', maxHeight: '90vh' }}>
                        <div className="dialog-header">
                            <h3>{modalMode === 'add' ? 'ADD CHARGE' : 'EDIT CHARGE'}</h3>
                            <button type="button" onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="dialog-body">
                            <form id="chargeForm" onSubmit={handleSave}>
                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                    <label className="required" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>DEPARTMENT</label>
                                    <Select2Dropdown
                                        options={departments.map(d => ({...d, text: d.text.toUpperCase()}))}
                                        value={currentCharge.department}
                                        onChange={(val) => setCurrentCharge({...currentCharge, department: val})}
                                        valueKey="id"
                                        displayKey="text"
                                        placeholder="SELECT DEPARTMENT"
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: '15px' }}>
                                    <label className="required" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>CHARGE NAME</label>
                                    <input 
                                        type="text" 
                                        value={currentCharge.name}
                                        onChange={(e) => setCurrentCharge({...currentCharge, name: e.target.value.toUpperCase()})}
                                        required
                                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
                                        placeholder="ENTER CHARGE NAME"
                                    />
                                </div>
                                <div style={{ marginBottom: '15px', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '24px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input 
                                            type="checkbox" 
                                            id="addVatCheck"
                                            checked={currentCharge.add_vat}
                                            onChange={(e) => setCurrentCharge({...currentCharge, add_vat: e.target.checked})}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <label htmlFor="addVatCheck" style={{ fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', margin: 0, lineHeight: 1, alignSelf: 'center' }}>ADD VAT</label>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input 
                                            type="checkbox" 
                                            id="cashCheck"
                                            checked={currentCharge.cash}
                                            onChange={(e) => setCurrentCharge({...currentCharge, cash: e.target.checked})}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <label htmlFor="cashCheck" style={{ fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', margin: 0, lineHeight: 1, alignSelf: 'center' }}>CASH/OFFICE/CHEQUE</label>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input 
                                            type="checkbox" 
                                            id="docRequiredCheck"
                                            checked={currentCharge.document_required}
                                            onChange={(e) => setCurrentCharge({...currentCharge, document_required: e.target.checked, document_types: e.target.checked ? currentCharge.document_types : ''})}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                        <label htmlFor="docRequiredCheck" style={{ fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', margin: 0, lineHeight: 1, alignSelf: 'center' }}>DOC REQUIRED</label>
                                        {currentCharge.document_required && (
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="ENTER DOCUMENT TYPES"
                                                value={currentCharge.document_types}
                                                onChange={(e) => setCurrentCharge({...currentCharge, document_types: e.target.value.toUpperCase()})}
                                                maxLength={30}
                                                required
                                                style={{ width: '160px', fontSize: '12px' }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="dialog-footer">
                            <button type="submit" form="chargeForm" className="dialog-btn dialog-btn-primary">SAVE</button>
                            <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => setIsModalOpen(false)}>CANCEL</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayableChargesList;
